/**
 * @fileoverview The Plant Doctor's small text helpers: whether the "In the
 * newest photo" line adds anything, a photo's short day, and the
 * extension-office line.
 *
 * Moved word for word out of PlantDoctor (audit batch 18, A107).
 *
 * Where: PlantDoctor (the window).
 * Words (en/es): plants.diagnose.extension.{lab,unsureOne,unsureTwo,unknown}.
 *
 * @module doctorText
 */

/** The share of the "In the newest photo" line's words already in the summary above which the line is hidden. */
const SEEING_REPEAT_SHARE = 0.6;

/**
 * The words of a sentence, lower-cased and without accents or punctuation.
 * @param {string} text - Any sentence.
 * @returns {string[]} Its words.
 */
const wordsOf = (text: string): string[] =>
  text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').split(/[^a-z0-9]+/).filter(Boolean);

/**
 * Whether the "In the newest photo" line is worth showing: not empty, and not
 * mostly words the summary already has (older check-ups were saved with the
 * line repeating the summary).
 * @param {string | null} seeing - The line on the newest photo.
 * @param {string | null} summary - "Its season so far".
 * @returns {boolean} True when the line adds something.
 */
export const seeingAddsSomething = (seeing: string | null, summary: string | null): boolean => {
  const words = wordsOf(seeing ?? '');
  if (words.length === 0) return false;
  const inSummary = new Set(wordsOf(summary ?? ''));
  const shared = words.filter((word) => inSummary.has(word)).length;
  return shared / words.length <= SEEING_REPEAT_SHARE;
};

/**
 * A photo's day in short form, in the gardener's language ("Jul 10",
 * "10 de jul").
 * @param {string} takenOn - The photo's date_taken (YYYY-MM-DD...).
 * @param {string} language - The app language.
 * @returns {string}
 */
export const shortPhotoDay = (takenOn: string, language: string): string => {
  const day = new Date(`${takenOn.slice(0, 10)}T12:00:00`);
  if (!language.startsWith('es')) return day.toLocaleDateString(language, { day: 'numeric', month: 'short' });
  const month = day.toLocaleDateString(language, { month: 'short' }).replace('.', '');
  return `${day.getDate()} de ${month}`;
};

/**
 * The extension-office line, saying why: a problem that needs a lab to tell
 * apart, Grow unsure between two or of its one guess, or nothing recognised.
 *
 * @param reason - The answer's extension reason (shown only when the answer points to the office).
 * @param t - The translator.
 * @returns The line.
 */
export function extensionText(
  reason: { kind: 'lab' | 'unsure' | 'unknown'; problem: string | null; other: string | null },
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (reason.kind === 'unknown' || !reason.problem) return t('plants.diagnose.extension.unknown');
  if (reason.kind === 'lab') return t('plants.diagnose.extension.lab', { problem: reason.problem.charAt(0).toUpperCase() + reason.problem.slice(1) });
  return reason.other
    ? t('plants.diagnose.extension.unsureTwo', { problem: reason.problem, other: reason.other })
    : t('plants.diagnose.extension.unsureOne', { problem: reason.problem });
}
